import { Button, Typography } from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { INACTIVITY_AUTO_LOGOUT_TIMEOUT } from "../utils/constant";

interface InactivityWarningModalProps {
  isOpen: boolean;
  onStayLoggedIn: () => void;
  onTimeout: () => void;
  countdownSeconds?: number;
}

export const InactivityWarningModal = ({
  isOpen,
  onStayLoggedIn,
  onTimeout,
  countdownSeconds = INACTIVITY_AUTO_LOGOUT_TIMEOUT / 1000,
}: InactivityWarningModalProps) => {
  const [remainingSeconds, setRemainingSeconds] = useState(countdownSeconds);

  useEffect(() => {
    if (!isOpen) {
      setRemainingSeconds(countdownSeconds);
      return;
    }

    // Start countdown
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);

          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, countdownSeconds]);

  // Trigger timeout callback when countdown reaches 0
  useEffect(() => {
    if (isOpen && remainingSeconds === 0) {
      onTimeout();
    }
  }, [isOpen, remainingSeconds, onTimeout]);

  if (!isOpen) {
    return null;
  }

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  // Handle button clicks with proper event handling
  const handleStayLoggedInClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    onStayLoggedIn();
  };

  return (
    <div
      className="absolute h-[100vh] w-[100%] bg-blue-gray-200 flex items-center justify-center z-[9999]"
      style={{ backgroundColor: "rgb(0,0,0, 0.7)" }}
      data-inactivity-modal="true"
      onClick={(e) => {
        // Prevent clicks on backdrop from propagating
        if (e.target === e.currentTarget) {
          e.stopPropagation();
        }
      }}
    >
      <div
        className="bg-white border-4 border-orange-500 rounded-md px-8 py-6 flex flex-col justify-center items-center shadow-2xl max-w-md w-full mx-4"
        data-inactivity-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-16 w-16 text-orange-500"
          >
            <path
              fillRule="evenodd"
              d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.401 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        <Typography className="font-bold text-3xl text-orange-700 mb-2 text-center">
          Session Timeout Warning
        </Typography>

        <Typography className="font-semibold text-lg text-gray-700 mb-4 text-center">
          You have been inactive for a while.
        </Typography>

        <Typography className="font-bold text-4xl text-red-600 mb-4">
          {formattedTime}
        </Typography>

        <Typography className="text-base text-gray-600 mb-6 text-center">
          Your session will be automatically logged out in{" "}
          {remainingSeconds > 60
            ? `${minutes} minute${minutes !== 1 ? "s" : ""}`
            : `${remainingSeconds} second${remainingSeconds !== 1 ? "s" : ""}`}
          . Click "Stay Logged In" to continue your session.
        </Typography>

        <div className="flex gap-4 w-full">
          <Button
            className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3"
            onClick={handleStayLoggedInClick}
            type="button"
          >
            Stay Logged In
          </Button>
        </div>
      </div>
    </div>
  );
};
